// Format a number using the current locale: thousands separator, locale decimal mark,
// and optional sig figs. Uses standard notation for |n| in [1e-5, 1e5) and scientific
// outside that range — so 100000 becomes "100,000" and 1e7 stays "1e7".
//
// `narrow` tightens that cutoff to [1e-4, 1e4) — for contexts too tight to fit standard-notation
// strings that are technically inside the normal range but still wide (e.g. "10,000" or
// "0.00001" both fit fine on desktop but overflow a phone-portrait preview box and collide with
// neighboring labels). Callers opt in per render context rather than this being a global default,
// since most callers (e.g. numerical block) have room for the wider range.

import { DEGREE_READOUT_DECIMALS } from '@agavi/blocks';

/**
 * A fixed number of decimal places, locale-aware.
 *
 * Deliberately distinct from `fmtLocale`, which caps SIGNIFICANT digits — so the decimals it shows
 * shrink as a number's magnitude grows. That's right for a measurement whose precision is relative,
 * and wrong whenever the decimal count is itself the meaningful thing. GPS coordinates are the
 * motivating case: at 6 significant figures a 3-digit longitude like -122.419416 renders as
 * -122.419, roughly 90 m of error, so how precise a student's readout looked depended on where on
 * Earth they were standing.
 *
 * Grouping is off: these are values where a thousands separator would be noise, and for coordinates
 * (always < 1000) it would never fire anyway.
 */
/**
 * `Intl.NumberFormat` is expensive to CONSTRUCT and cheap to reuse, and these formatters are called
 * from render paths — axis ticks, chip labels, answer bars — so the constructor was running
 * hundreds of times per frame. A profile of typing into a feedback box had `fmtLocale` at 1.3% of
 * samples on its own, which is a lot for a function that only formats a number.
 *
 * The key is the locale plus the options, so two callers asking for different digits never share
 * one. The set of distinct keys is small and fixed by the code, not by user input, so this cannot
 * grow without bound.
 */
const FORMATTERS = new Map<string, Intl.NumberFormat>();

function formatter(locale: string, opts: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = locale + '|' + JSON.stringify(opts);
  let f = FORMATTERS.get(key);
  if (!f) {
    f = new Intl.NumberFormat(locale, opts);
    FORMATTERS.set(key, f);
  }
  return f;
}

export function fmtFixed(n: number, locale: string, decimals: number): string {
  return formatter(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: false,
  }).format(n);
}

/**
 * THE THRESHOLD, in one place.
 *
 * `fmtLocale` had it inline, which was fine while formatting was the only caller. The tolerance
 * INPUT now wants the same answer — a box reading 250000 beside a chip reading 2.5e5 is the
 * same number spelled two ways, which is what the convention exists to prevent — and two copies
 * of a pair of cutoffs is two chances for them to disagree.
 */
export function wantsScientific(n: number, narrow = false): boolean {
  const abs = Math.abs(n);
  return n !== 0 && (abs < (narrow ? 1e-4 : 1e-5) || abs >= (narrow ? 1e4 : 1e5));
}

/**
 * WHAT A SCIENTIFIC-GRAMMAR INPUT SHOULD HOLD, or null to leave it exactly as typed.
 *
 * Locale-free on purpose, unlike `fmtLocale`. This string is STORED and re-parsed, and a
 * pt-BR `2,5e5` would be normalised back to a dot on the next keystroke — so the box would
 * disagree with itself. The separator a teacher types is still accepted; only what is written
 * back is canonical.
 *
 * Null for anything that is not a complete number, so a half-typed `1e` is left alone rather
 * than being rewritten mid-thought into something else.
 */
export function canonicalScientificInput(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || !wantsScientific(n)) return null;
  // `toExponential()` with no argument keeps as many digits as the value needs, so nothing is
  // rounded away — 250000 becomes 2.5e+5, not 2.50e+5.
  const out = n.toExponential().replace('e+', 'e');
  return out === trimmed ? null : out;
}

export function fmtLocale(n: number, locale: string, sigFigs?: number, forceScientific = false, narrow = false): string {
  // Round first so the notation decision reflects the displayed value, not the raw one.
  // e.g. 98500 at 1 sig fig rounds to 100000, which belongs in scientific notation —
  // matching 101500 at 1 sig fig so the collapse check works correctly.
  const rounded = parseFloat(n.toPrecision(sigFigs ?? 6));
  const notation: Intl.NumberFormatOptions['notation'] =
    forceScientific || wantsScientific(rounded, narrow) ? 'scientific' : 'standard';
  const opts: Intl.NumberFormatOptions = { notation };
  if (sigFigs != null) {
    opts.minimumSignificantDigits = sigFigs;
    opts.maximumSignificantDigits = sigFigs;
  } else {
    opts.maximumSignificantDigits = 6;
  }
  return formatter(locale, opts).format(n).replace(/E\+?/, 'e');
}

/**
 * A sensor angle or heading as a human would read it off an instrument: at most one decimal.
 *
 * `fmtLocale`'s default of 6 SIGNIFICANT digits is wrong for these. A simulated "way off" angle came
 * out as 79.8568°, and a compass tolerance edge as 74.1406° — four decimals of pure arithmetic
 * residue from the scenario maths. No inclinometer or magnetometer on a phone resolves better than
 * about a degree, so that precision is not just noise, it actively misrepresents the instrument.
 *
 * Not `fmtFixed`, which would pad a clean 30 into "30.0" — a whole degree should read as a whole
 * degree. Grouping is off: these are always well under 1000.
 *
 * The DECIMALS are shared with `resolveAnswerRef`, which rounds a recalled reading to the same
 * precision. They are one claim about the same instruments, and two copies of it is how a
 * sentence quoting a block came to print `183.4621` while the block showed `183.5`.
 */
export function fmtDegrees(deg: number, locale: string): string {
  return formatter(locale, { maximumFractionDigits: DEGREE_READOUT_DECIMALS, useGrouping: false })
    .format(deg);
}

/**
 * A formatted number as it should be SPOKEN, when scientific notation is in it.
 *
 * "5e9" reaches a screen reader as "five ee nine", which is not a number. The written form is a
 * shorthand for "5 times 10 to the 9", and that is the reading — the same principle already settled
 * for formulas, where the raw source is never what gets announced.
 *
 * Takes the ALREADY-FORMATTED string rather than the number, and this is the point of it: the
 * spoken value is then guaranteed to be the displayed value said aloud, whatever precision, locale
 * or notation threshold produced it. Deriving it from the number again would be a second formatter
 * free to disagree with the first.
 *
 * Deliberately NOT scale words. "5 billion" is better English for this one case and worse
 * everywhere else: nothing in the language says 10^23, so Avogadro and electron charge would need
 * this form anyway, and "billion" is 10^9 on the short scale and 10^12 on the long one — a
 * factor-of-1000 hazard in a science tool. It would also say a different number than the screen
 * shows, which is exactly the drift the shared formatter exists to prevent.
 */
export function speakNumber(
  formatted: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  // `fmtLocale` normalises the exponent marker to a lowercase `e` and drops the `+`; the mantissa
  // keeps whatever decimal separator the locale gave it, which is why nothing here re-parses the
  // number. The optional `+` is still accepted, because a value that reached this without going
  // through `fmtLocale` carries JS's own `String(6.02e23)` form — "6.02e+23".
  const m = /^(.*?)e\+?(-?)(\d+)$/i.exec(formatted);
  if (!m) return formatted;
  const [, mantissa, sign, digits] = m;
  // The sign as a WORD. A bare "-19" is read as "dash 19" by some engines and swallowed by others,
  // and a sign lost from an exponent is the one error that cannot be heard as an error.
  const exponent = sign ? t('common.spokenNegativeExponent', { exponent: digits }) : digits!;
  return t('common.spokenScientific', { mantissa: mantissa!, exponent });
}

/**
 * Moved to `@agavi/blocks` (see number-grammar.ts) and re-exported here so existing importers and
 * the `filterDecimalText` name both keep working. It is a grammar, not a formatter.
 */
export { filterDecimalText } from '@agavi/blocks';
